import { produce } from 'immer'
import { omit } from 'lodash'
import { useState, useRef, useMemo } from 'react'
import {
  type InheritanceResource,
  type MenuOption,
  PrivacyState,
  type ReadableAccessMetadata,
  type UserLike,
  type WritableAccessMetadata,
} from '@opallabs/concepts'

const privacySelectOptions: Array<MenuOption> = [
  {
    label: 'Entire Workspace',
    value: PrivacyState.Public,
  },
  {
    label: 'Specific people & spaces',
    value: PrivacyState.Private,
  },
]

type SharingStateArgs = {
  accessMetadata?: ReadableAccessMetadata
  onChangeAccess: (access: WritableAccessMetadata) => Promise<void>
  ownerId?: string
  membershipInheritedFromWorkspace: boolean
}

type SharingStateValue = {
  privacyState: PrivacyState
  hasUnsavedChanges: boolean
  onAddCollaborator: (user: UserLike) => void
  onChangeInheritance: (
    resource: InheritanceResource,
    inheritsMembership: boolean
  ) => void
  onRemoveCollaborator: (user: UserLike) => void
  onSave: () => Promise<void>
  onSelectPrivacy: (value: PrivacyState) => void
  privacySelectOptions: Array<MenuOption>
  selectedPrivacyOption: MenuOption
}

const isIncluded = (item: UserLike, itemList: UserLike[]) => {
  const itemIds = itemList.map((user) => user.uuid)
  return itemIds.includes(item.uuid)
}

export const useSharingSaveState = ({
  accessMetadata,
  onChangeAccess,
  ownerId,
  membershipInheritedFromWorkspace,
}: SharingStateArgs): SharingStateValue => {
  const [initialAccessMetadata, setInitialAccessMetadata] =
    useState(accessMetadata)
  const initialPrivacyStatus = useRef<PrivacyState>(
    membershipInheritedFromWorkspace
      ? PrivacyState.Public
      : PrivacyState.Private
  )
  const [privacyState, setPrivacyStatus] = useState<PrivacyState>(
    initialPrivacyStatus.current
  )
  const [addedCollaborators, setAddedCollaborators] = useState<UserLike[]>([])
  const [removedCollaborators, setRemovedCollaborators] = useState<UserLike[]>(
    []
  )
  const [inheritanceChanges, setInheritanceChanges] = useState<
    Record<string, boolean>
  >({})

  const selectedPrivacyOption = useMemo((): MenuOption => {
    const index = privacyState === PrivacyState.Private ? 1 : 0
    return privacySelectOptions[index]
  }, [privacyState])

  const hasUnsavedChanges = useMemo(
    () =>
      addedCollaborators.length > 0 ||
      removedCollaborators.length > 0 ||
      Object.keys(inheritanceChanges).length > 0 ||
      privacyState !== initialPrivacyStatus.current,
    [
      addedCollaborators.length,
      removedCollaborators.length,
      inheritanceChanges,
      privacyState,
    ]
  )

  const onAddCollaborator = (user: UserLike) => {
    if (isIncluded(user, removedCollaborators)) {
      return setRemovedCollaborators(
        removedCollaborators.filter((existing) => existing.uuid !== user.uuid)
      )
    }
    setAddedCollaborators([...addedCollaborators, user])
  }
  const onRemoveCollaborator = (user: UserLike) => {
    if (ownerId && user.uuid === ownerId) {
      return
    }

    if (isIncluded(user, addedCollaborators)) {
      return setAddedCollaborators(
        addedCollaborators.filter((existing) => existing.uuid !== user.uuid)
      )
    }
    setRemovedCollaborators([...removedCollaborators, user])
  }
  const onChangeInheritance = (
    resource: InheritanceResource,
    inheritsMembership: boolean
  ) => {
    if (resource.uuid in inheritanceChanges) {
      setInheritanceChanges((prev) => omit(prev, [resource.uuid]))
    } else {
      setInheritanceChanges((prev) => ({
        ...prev,
        [resource.uuid]: inheritsMembership,
      }))
    }
  }

  const isResourceInheriting = (
    resource: ReadableAccessMetadata['resources'][number]
  ): boolean => {
    if (resource.type === 'workspace') {
      return privacyState === PrivacyState.Public
    }

    // Inheritance changes for other resources are only allowed
    // if the resource being modified is currently private
    return privacyState === PrivacyState.Private
      ? (inheritanceChanges[resource.id] ?? resource.meta.inherits_membership)
      : resource.meta.inherits_membership
  }

  const saveAccessChanges = async () => {
    if (!initialAccessMetadata) {
      return
    }

    const { users, groups, resources } = initialAccessMetadata
    const baseState: WritableAccessMetadata = {
      users: [...users],
      groups: groups.map(({ id, type }) => ({ id, type })),
      resources: resources.map((resource) => ({
        ...resource,
        meta: { inherits_membership: isResourceInheriting(resource) },
      })),
    }

    const newAccessMetadata =
      privacyState === PrivacyState.Private
        ? applyCollaboratorChanges(baseState)
        : baseState

    return onChangeAccess(newAccessMetadata)
  }

  const applyCollaboratorChanges = (state: WritableAccessMetadata) => {
    // Returns true if the user is not in `removedCollaborators`
    const filterRemovedCollaborator =
      <T extends 'user' | 'user_group'>(type: T) =>
      (user: { id: string; type: T }) =>
        !removedCollaborators.find(
          (removed) => removed.type === type && removed.uuid === user.id
        )

    return produce(state, (draftState) => {
      for (const collaborator of addedCollaborators) {
        switch (collaborator.type) {
          case 'user':
            draftState.users!.push({
              id: collaborator.uuid,
              type: 'user',
            })
            break
          case 'user_group':
            draftState.groups!.push({
              id: collaborator.uuid,
              type: 'user_group',
            })
        }
      }

      if (removedCollaborators.length) {
        draftState.users = draftState.users!.filter(
          filterRemovedCollaborator('user')
        )
        draftState.groups = draftState.groups!.filter(
          filterRemovedCollaborator('user_group')
        )
      }
    })
  }

  const onSave = async () => {
    await saveAccessChanges()
    setInitialAccessMetadata(accessMetadata)
  }

  return {
    privacyState,
    hasUnsavedChanges,
    onAddCollaborator,
    onChangeInheritance,
    onRemoveCollaborator,
    onSave,
    onSelectPrivacy: setPrivacyStatus,
    privacySelectOptions,
    selectedPrivacyOption,
  }
}
